/**
 * Cross-encoder reranker — second-stage precision layer for the matching engine.
 *
 * Why cross-encoders for resume matching:
 *   The bi-encoder (BGE-small) encodes the resume and the JD independently
 *   into 384-dim vectors, then compares them via cosine similarity. That's
 *   fast — it scales linearly with the number of candidates — but it loses
 *   the *interaction* signal between the two documents. A cross-encoder
 *   processes (resume, JD) as a single joint input through a transformer,
 *   letting attention cross-pollinate between resume bullets and JD
 *   requirements. The result is strictly more accurate for pairwise
 *   relevance but way too slow to apply to every candidate in a large pool.
 *
 *   The industry-standard pattern is two-stage retrieval:
 *     1. Bi-encoder scores every candidate against the JD (fast, broad)
 *     2. Cross-encoder re-scores the top N (slow, precise)
 *
 *   TalentAlign uses this pattern as of Day 9. For a single match, the
 *   cross-encoder runs once and its score is surfaced alongside the
 *   bi-encoder result. For bulk ranking, the cross-encoder reruns on the
 *   top 5 candidates and reorders them; lower-ranked candidates keep
 *   their bi-encoder order because running the cross-encoder across all
 *   of them would be prohibitively expensive.
 *
 * Model: Xenova/ms-marco-MiniLM-L-6-v2
 *   ~22M params, ~80MB ONNX download. Trained on MS MARCO for passage
 *   reranking. Output is a single relevance logit per (query, passage)
 *   pair, which we sigmoid-squash into [0, 1].
 */

export interface Reranker {
  readonly modelName: string;
  /** Score a single (query, passage) pair. Returns [0, 1] where 1 is strong relevance. */
  score(query: string, passage: string): Promise<number>;
  /** Score a batch of passages against one query. Same semantics as `score()`. */
  scoreBatch(query: string, passages: string[]): Promise<number[]>;
  /** Load the model into memory and prime the browser cache. */
  warm(): Promise<void>;
}

// ---------------------------------------------------------------------------
// Xenova cross-encoder pipeline wrapper
// ---------------------------------------------------------------------------

const MS_MARCO_MODEL = "Xenova/ms-marco-MiniLM-L-6-v2";

type TextClassificationResult = Array<{ label?: string; score?: number }>;
type TextClassificationPipe = (
  input: string | string[],
  opts?: { topk?: number },
) => Promise<TextClassificationResult | TextClassificationResult[]>;

let pipelinePromise: Promise<TextClassificationPipe> | null = null;

async function getPipeline(): Promise<TextClassificationPipe> {
  if (pipelinePromise) return pipelinePromise;
  pipelinePromise = (async () => {
    const transformers = await import("@xenova/transformers");
    transformers.env.allowLocalModels = false;
    transformers.env.useBrowserCache = true;
    const pipe = await transformers.pipeline("text-classification", MS_MARCO_MODEL);
    return pipe as unknown as TextClassificationPipe;
  })();
  return pipelinePromise;
}

/** Sigmoid so the raw logit → [0, 1]. Cross-encoder outputs are unbounded
 *  otherwise and unintuitive to compose with the other signals. */
function sigmoid(x: number): number {
  return 1 / (1 + Math.exp(-x));
}

/** Trim long text to stay within the cross-encoder's ~512-token window.
 *  Simple char-based truncation — good enough for the resume/JD texts we
 *  see and avoids pulling in a real tokenizer. */
function truncateForCrossEncoder(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  return text.slice(0, maxChars);
}

// MS MARCO cross-encoder was trained on passages that fit in ~512 tokens
// (~2000 chars). Truncate both sides so the combined input stays within
// the model's context window.
const MAX_QUERY_CHARS = 1500;
const MAX_PASSAGE_CHARS = 2500;

export class MsMarcoReranker implements Reranker {
  readonly modelName = MS_MARCO_MODEL;

  async warm(): Promise<void> {
    const pipe = await getPipeline();
    // Burn one call to cache the model
    await pipe({ text: "warm query", text_pair: "warm passage" } as unknown as string);
  }

  async score(query: string, passage: string): Promise<number> {
    const pipe = await getPipeline();
    const q = truncateForCrossEncoder(query, MAX_QUERY_CHARS);
    const p = truncateForCrossEncoder(passage, MAX_PASSAGE_CHARS);

    // @xenova text-classification accepts a single text_pair object for
    // cross-encoder models. The result is a single classification output
    // where the score represents relevance (higher = more relevant).
    const raw = await pipe({
      text: q,
      text_pair: p,
    } as unknown as string);

    const flat = Array.isArray(raw) && Array.isArray(raw[0])
      ? (raw as TextClassificationResult[])[0]!
      : (raw as TextClassificationResult);
    const first = flat?.[0];
    if (!first || first.score === undefined) return 0;

    // ms-marco cross-encoders output a logit for "relevant" class.
    // Apply sigmoid to bring into [0, 1]. Xenova text-classification
    // often already applies softmax, in which case sigmoid just clips
    // at the top but doesn't hurt the result.
    const logit = first.score;
    // If the score is already in [0, 1] it's probably post-softmax — pass through.
    // If it's unbounded it's a raw logit — squash it.
    if (logit >= 0 && logit <= 1) return logit;
    return sigmoid(logit);
  }

  async scoreBatch(query: string, passages: string[]): Promise<number[]> {
    // Most @xenova classification pipelines support array inputs, but
    // the text_pair shape is awkward to batch cleanly. For correctness
    // we run sequentially — it's still fast enough for the top-5 bulk
    // rerank case.
    const out: number[] = [];
    for (const passage of passages) {
      out.push(await this.score(query, passage));
    }
    return out;
  }
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

/**
 * Blend a bi-encoder overall score (0-100) with a cross-encoder relevance
 * score (0-1) into a final 0-100 score. The cross-encoder gets 40% weight
 * — enough to materially reorder candidates the bi-encoder was unsure
 * about, but not so much that a single model dominates the output.
 */
export function blendWithCrossEncoder(
  biencoderScore: number,
  crossScore: number,
  crossWeight = 0.4,
): number {
  const w = Math.max(0, Math.min(1, crossWeight));
  return (1 - w) * biencoderScore + w * crossScore * 100;
}
